import { useCallback, useEffect, useRef, useState } from "react";
import { useRafCoalescedState } from "../useRafCoalescedState";
import { calculateFlexValuesFromDelta } from "./usePanelResize.calculateFlex";
import {
  FLEX_EPSILON,
  MIN_FLEX,
  PANEL_RESIZING_CLASS,
} from "./usePanelResize.constants";
import {
  extractRangeConstraints,
  getConstraintValue,
} from "./usePanelResize.constraints";
import {
  getFlexValue,
  normalizeFlexValues,
  setFlexValue,
} from "./usePanelResize.flexDom";
import {
  applyPixelConstraintsToFlexPair,
  isCssSizeFrozen,
} from "./usePanelResize.pixelConstraints";
import type {
  DragState,
  UsePanelResizeParams,
  UsePanelResizeReturn,
} from "./usePanelResize.types";
import { useRangeConstraintValidation } from "./useRangeConstraintValidation";

export type {
  UsePanelResizeParams,
  UsePanelResizeReturn,
} from "./usePanelResize.types";

/**
 * Hook for interactive panel resizing via drag handles
 *
 * Enables users to click and drag a resize handle to adjust flex values
 * of adjacent panels in a flex container layout.
 *
 * @param params - Configuration object with handler ref, panel refs, and options
 * @returns Object with isResizing state and current flexValues
 */
export const usePanelResize = ({
  handlerRef,
  panelsRefs: [firstPanelRef, secondPanelRef],
  direction = "horizontal",
  initialFlexValues,
  range,
  onResizeEnd,
  disabled = false,
  dragSensitivity = 1,
}: UsePanelResizeParams): UsePanelResizeReturn => {
  const [isResizing, setIsResizing] = useState(false);
  const [flexValues, setFlexValues] = useState<[number, number]>(
    initialFlexValues ?? [1, 1]
  );

  /**
   * Increment to trigger a re-render so the mousedown sync effect can re-evaluate.
   * Used by destroy() and rebind() since they mutate isDestroyedRef (non-reactive).
   */
  const [, forceUpdate] = useState(0);

  const isVertical = direction === "vertical";

  /** Always-current ref for onResizeEnd, avoiding stale closures mid-drag */
  const onResizeEndRef = useRef(onResizeEnd);
  onResizeEndRef.current = onResizeEnd;

  /**
   * Stores active drag state during resize operation
   * Uses ref to avoid re-renders during mouse movement
   */
  const dragStateRef = useRef<DragState | null>(null);

  const { scheduleUpdate: scheduleFlexUpdate, flush: flushPendingFlex } =
    useRafCoalescedState<[number, number]>(setFlexValues);

  /**
   * Stores cleanup functions for global event listeners
   * Stored per-instance to support multiple simultaneous resize operations
   */
  const cleanupRef = useRef<{
    removeMouseMove?: () => void;
    removeMouseUp?: () => void;
  }>({});

  /**
   * Tracks whether the hook has been manually destroyed
   * Used to prevent automatic reattachment after destroy() is called
   */
  const isDestroyedRef = useRef(false);

  /**
   * Tracks whether flex values have been applied to the panels.
   * Guards the sync-on-every-render init effect so it only runs once
   * (reset by initialFlexValues/disabled changes and by rebind()).
   */
  const isFlexInitializedRef = useRef(false);

  /**
   * Tracks the handler element and listener that are currently registered.
   * Allows the sync effect to detect element swaps and re-register correctly.
   */
  const lastRegisteredRef = useRef<{
    handler: HTMLElement | null;
    listener: ((e: MouseEvent) => void) | null;
  }>({ handler: null, listener: null });

  /**
   * Centralized cleanup utility - removes event listeners and resets UI state
   * Used by onMouseUp, destroy, and cleanup effect
   */
  const performCleanup = useCallback(() => {
    // Remove global listeners
    cleanupRef.current.removeMouseMove?.();
    cleanupRef.current.removeMouseUp?.();
    cleanupRef.current.removeMouseMove = undefined;
    cleanupRef.current.removeMouseUp = undefined;

    // Flush any coalesced flex update so final values are in React state
    flushPendingFlex();

    // Restore user selection and remove resizing class
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    document.body.classList.remove(PANEL_RESIZING_CLASS);

    // Clear drag state
    dragStateRef.current = null;
  }, [flushPendingFlex]);

  useRangeConstraintValidation(range, isVertical, firstPanelRef);

  // Reset flag when inputs change, allowing re-initialization
  useEffect(() => {
    isFlexInitializedRef.current = false;
  }, [initialFlexValues, disabled]);

  /**
   * Sync flex initialization on every render (same pattern as mousedown sync).
   * Must be declared BEFORE the mousedown sync effect so flex values are applied
   * before the first drag can read them.
   */
  useEffect(() => {
    if (disabled || isDestroyedRef.current || isFlexInitializedRef.current) return;

    const firstPanel = firstPanelRef.current;
    const secondPanel = secondPanelRef.current;
    if (!firstPanel || !secondPanel) return;

    if (initialFlexValues) {
      setFlexValue(firstPanel, initialFlexValues[0]);
      setFlexValue(secondPanel, initialFlexValues[1]);
      setFlexValues(initialFlexValues);
    } else {
      const values = normalizeFlexValues([
        getFlexValue(firstPanel),
        getFlexValue(secondPanel),
      ]);
      setFlexValue(firstPanel, values[0]);
      setFlexValue(secondPanel, values[1]);
      setFlexValues(values);
    }

    isFlexInitializedRef.current = true;
  });

  /**
   * Removes inline flex styles from both panels
   * Restores original CSS without affecting event listeners
   */
  const removeSizes = useCallback(() => {
    const firstPanel = firstPanelRef.current;
    const secondPanel = secondPanelRef.current;

    if (firstPanel) {
      firstPanel.style.flex = "";
    }
    if (secondPanel) {
      secondPanel.style.flex = "";
    }
  }, []);

  /**
   * Applies flex values to panels
   * If no values provided, uses initialFlexValues from hook params
   * If no initialFlexValues, reads from DOM with minimum value of 1
   */
  const applyFlexValues = useCallback(
    (values?: [number, number]) => {
      const firstPanel = firstPanelRef.current;
      const secondPanel = secondPanelRef.current;

      if (!firstPanel || !secondPanel) return;

      let nextFlexValues: [number, number];

      if (values) {
        nextFlexValues = values;
      } else if (initialFlexValues) {
        nextFlexValues = initialFlexValues;
      } else {
        const firstFlex = getFlexValue(firstPanel);
        const secondFlex = getFlexValue(secondPanel);

        nextFlexValues = normalizeFlexValues([firstFlex, secondFlex]);
      }

      setFlexValue(firstPanel, nextFlexValues[0]);
      setFlexValue(secondPanel, nextFlexValues[1]);

      setFlexValues(nextFlexValues);
    },
    [initialFlexValues]
  );

  const calculateFlexValues = useCallback(
    (
      delta: number,
      totalSize: number,
      initialValues: [number, number]
    ): [number, number] =>
      calculateFlexValuesFromDelta(
        delta,
        totalSize,
        initialValues,
        range,
        dragSensitivity,
        MIN_FLEX
      ),
    [range, dragSensitivity]
  );

  /**
   * Updates flex values on panel elements via direct DOM manipulation.
   * Enforces pixel constraints by measuring actual rendered sizes AFTER application.
   * Detects external CSS constraints (max-height, min-height, etc.) by comparing
   * rendered sizes before and after — reverts flex when the layout didn't actually move.
   *
   * @returns `true` if a CSS-freeze revert happened (caller must skip constraint boundary recording)
   */
  const updateFlexValues = useCallback(
    (values: [number, number]): boolean => {
      const firstPanel = firstPanelRef.current;
      const secondPanel = secondPanelRef.current;

      if (!firstPanel || !secondPanel) return false;

      const prevFirstFlex = getFlexValue(firstPanel);
      const prevSecondFlex = getFlexValue(secondPanel);
      const measureSize = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        return isVertical ? rect.height : rect.width;
      };
      const preFirstSize = measureSize(firstPanel);
      const preSecondSize = measureSize(secondPanel);

      let [newFirstFlex, newSecondFlex] = values;

      setFlexValue(firstPanel, newFirstFlex);
      setFlexValue(secondPanel, newSecondFlex);

      if (range) {
        const { firstMin, secondMin, firstMax, secondMax } =
          extractRangeConstraints(range);

        const firstMinVal = getConstraintValue(firstMin);
        const firstMaxVal = getConstraintValue(firstMax);
        const secondMinVal = getConstraintValue(secondMin);
        const secondMaxVal = getConstraintValue(secondMax);

        const hasPixelConstraints =
          firstMinVal?.isPixel ||
          firstMaxVal?.isPixel ||
          secondMinVal?.isPixel ||
          secondMaxVal?.isPixel;

        if (hasPixelConstraints) {
          const firstPanelRect = firstPanel.getBoundingClientRect();
          const secondPanelRect = secondPanel.getBoundingClientRect();

          const firstPanelSize = isVertical
            ? firstPanelRect.height
            : firstPanelRect.width;
          const secondPanelSize = isVertical
            ? secondPanelRect.height
            : secondPanelRect.width;

          const {
            first: nf,
            second: ns,
            needsReapply,
          } = applyPixelConstraintsToFlexPair(
            newFirstFlex,
            newSecondFlex,
            firstPanelSize,
            secondPanelSize,
            firstMinVal,
            firstMaxVal,
            secondMinVal,
            secondMaxVal,
            MIN_FLEX
          );
          newFirstFlex = nf;
          newSecondFlex = ns;

          if (needsReapply) {
            setFlexValue(firstPanel, newFirstFlex);
            setFlexValue(secondPanel, newSecondFlex);
          }
        }
      }

      // Detect external CSS constraints (max-height, min-height, max-width, min-width).
      // isCssSizeFrozen checks both size response AND getComputedStyle for an actual
      // CSS constraint, avoiding false positives from flex-basis:auto zero-free-space layouts.
      const postFirstSize = measureSize(firstPanel);
      const postSecondSize = measureSize(secondPanel);
      const frozen = isCssSizeFrozen(
        prevFirstFlex,
        prevSecondFlex,
        newFirstFlex,
        newSecondFlex,
        preFirstSize,
        preSecondSize,
        postFirstSize,
        postSecondSize,
        firstPanel,
        secondPanel,
        isVertical
      );

      if (frozen) {
        setFlexValue(firstPanel, prevFirstFlex);
        setFlexValue(secondPanel, prevSecondFlex);
        scheduleFlexUpdate([prevFirstFlex, prevSecondFlex]);
        return true;
      }

      scheduleFlexUpdate([newFirstFlex, newSecondFlex]);
      return false;
    },
    [range, isVertical, scheduleFlexUpdate]
  );

  /**
   * Keep dragState.totalSize current when the container is resized.
   *
   * totalSize is captured at mousedown. If the window (or any ancestor) is resized
   * during an active drag, the snapshot becomes stale and pixel→flex calculations
   * produce wrong deltas. A ResizeObserver on the container is event-driven (zero
   * per-frame cost), updating the stored value only when the container actually changes.
   * Outside of an active drag dragStateRef.current is null so the observer is a no-op.
   */
  useEffect(() => {
    const container = firstPanelRef.current?.parentElement;
    if (!container) return;

    const observer = new ResizeObserver(([entry]) => {
      if (!dragStateRef.current) return;
      const rect = entry.contentRect;
      dragStateRef.current.totalSize = isVertical ? rect.height : rect.width;
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [isVertical]);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      const firstPanel = firstPanelRef.current;
      const secondPanel = secondPanelRef.current;
      if (!firstPanel || !secondPanel) return;

      const currentMousePos = isVertical ? e.clientY : e.clientX;
      const {
        constraintHitAtMousePos,
        constrainedFlexValues,
        constraintWasNegativeDelta,
      } = dragState;

      if (constraintHitAtMousePos !== null && constrainedFlexValues !== null) {
        const hasCrossed = constraintWasNegativeDelta
          ? currentMousePos > constraintHitAtMousePos
          : currentMousePos < constraintHitAtMousePos;

        if (!hasCrossed) {
          return;
        }

        const deltaBeyondBoundary = currentMousePos - constraintHitAtMousePos;

        dragState.constraintHitAtMousePos = null;
        dragState.constrainedFlexValues = null;

        const [newFirstFlex, newSecondFlex] = calculateFlexValues(
          deltaBeyondBoundary,
          dragState.totalSize,
          constrainedFlexValues
        );

        const froze = updateFlexValues([newFirstFlex, newSecondFlex]);

        // CSS freeze: boundary was already cleared above — leave it cleared so the
        // next frame enters the normal path (rawDelta from initialMousePos).
        if (!froze) {
          const actualFirstFlex = getFlexValue(firstPanel);
          const expectedFirstFlex = newFirstFlex;

          if (Math.abs(actualFirstFlex - expectedFirstFlex) > FLEX_EPSILON) {
            dragState.constraintHitAtMousePos = currentMousePos;
            dragState.constrainedFlexValues = [
              actualFirstFlex,
              getFlexValue(secondPanel),
            ];
            dragState.constraintWasNegativeDelta =
              actualFirstFlex > expectedFirstFlex;
          }
        }

        return;
      }

      const rawDelta = currentMousePos - dragState.initialMousePos;

      const newValues = calculateFlexValues(
        rawDelta,
        dragState.totalSize,
        dragState.initialFlexValues
      );

      const froze = updateFlexValues(newValues);

      // CSS freeze: skip boundary recording so the next frame recalculates from
      // initialMousePos. As the user reverses, rawDelta naturally decreases until
      // the computed flex drops below the CSS cap and layout responds.
      if (!froze) {
        const actualFirstFlex = getFlexValue(firstPanel);
        const expectedFirstFlex = newValues[0];

        if (Math.abs(actualFirstFlex - expectedFirstFlex) > FLEX_EPSILON) {
          dragState.constraintHitAtMousePos = currentMousePos;
          dragState.constrainedFlexValues = [
            actualFirstFlex,
            getFlexValue(secondPanel),
          ];
          dragState.constraintWasNegativeDelta = actualFirstFlex > expectedFirstFlex;
        }
      }
    },
    [calculateFlexValues, updateFlexValues, isVertical]
  );

  const onMouseUp = useCallback(
    (_e: MouseEvent) => {
      const dragState = dragStateRef.current;
      const handlerElem = handlerRef.current;

      handlerElem?.classList.remove("dragging");

      performCleanup();

      setIsResizing(false);

      if (dragState && onResizeEndRef.current) {
        const firstPanel = firstPanelRef.current;
        const secondPanel = secondPanelRef.current;
        if (firstPanel && secondPanel) {
          const finalValues: [number, number] = [
            getFlexValue(firstPanel),
            getFlexValue(secondPanel),
          ];
          onResizeEndRef.current(finalValues[0], finalValues[1]);
        }
      }
    },
    [performCleanup]
  );

  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button !== 0) return;

      if (disabled) return;

      const handlerElem = handlerRef.current;
      const firstPanel = firstPanelRef.current;
      const secondPanel = secondPanelRef.current;

      if (!handlerElem || !firstPanel || !secondPanel) return;

      e.preventDefault();

      const container = handlerElem.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();

      const initialMousePos = isVertical ? e.clientY : e.clientX;

      const totalSize = isVertical ? containerRect.height : containerRect.width;

      if (totalSize <= 0) {
        console.warn("[usePanelResize] Cannot resize: container has zero size");
        return;
      }

      const rawFirstFlex = getFlexValue(firstPanel);
      const rawSecondFlex = getFlexValue(secondPanel);

      const dragInitialFlexValues = normalizeFlexValues([
        rawFirstFlex,
        rawSecondFlex,
      ]);

      dragStateRef.current = {
        initialMousePos,
        initialFlexValues: dragInitialFlexValues,
        totalSize,
        direction,
        constraintHitAtMousePos: null,
        constrainedFlexValues: null,
        constraintWasNegativeDelta: false,
      };

      handlerElem.classList.add("dragging");

      document.body.style.userSelect = "none";
      document.body.style.cursor = (isVertical ? "ns" : "ew") + "-resize";
      document.body.classList.add(PANEL_RESIZING_CLASS);

      setIsResizing(true);

      window?.addEventListener("mousemove", onMouseMove, { capture: true });
      window?.addEventListener("mouseup", onMouseUp, { capture: true });

      cleanupRef.current.removeMouseMove = () => {
        window?.removeEventListener("mousemove", onMouseMove, {
          capture: true,
        });
      };
      cleanupRef.current.removeMouseUp = () => {
        window?.removeEventListener("mouseup", onMouseUp, { capture: true });
      };
    },
    [direction, disabled, onMouseMove, onMouseUp]
  );

  const destroy = useCallback(() => {
    performCleanup();

    removeSizes();

    setIsResizing(false);

    isDestroyedRef.current = true;
    forceUpdate((n) => n + 1);
  }, [performCleanup, removeSizes]);

  const rebind = useCallback(() => {
    if (disabled) return;

    isDestroyedRef.current = false;
    isFlexInitializedRef.current = false;

    forceUpdate((n) => n + 1);
  }, [disabled]);

  useEffect(() => {
    const { handler: prevHandler, listener: prevListener } =
      lastRegisteredRef.current;
    const nextHandler = handlerRef.current;

    const shouldDetach =
      prevHandler &&
      prevListener &&
      (prevHandler !== nextHandler ||
        prevListener !== onMouseDown ||
        disabled ||
        isDestroyedRef.current);

    if (shouldDetach) {
      prevHandler.removeEventListener("mousedown", prevListener);
      lastRegisteredRef.current = { handler: null, listener: null };
    }

    const canAttach =
      nextHandler &&
      !disabled &&
      !isDestroyedRef.current &&
      lastRegisteredRef.current.handler === null;

    if (canAttach) {
      nextHandler.addEventListener("mousedown", onMouseDown);
      lastRegisteredRef.current = { handler: nextHandler, listener: onMouseDown };
    }
  });

  useEffect(
    () => () => {
      const { handler, listener } = lastRegisteredRef.current;
      if (handler && listener) {
        handler.removeEventListener("mousedown", listener);
        lastRegisteredRef.current = { handler: null, listener: null };
      }

      performCleanup();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    isResizing,
    flexValues,
    removeSizes,
    applyFlexValues,
    destroy,
    rebind,
  };
};
